import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useDemoRole } from '../contexts/DemoRoleContext';
import { useUserOrganizations } from '../assistants/drew/hooks/useOrganization';

const COLLECTION = 'Hugo_Notifications';

/**
 * useNotificationFeed — powers the top-bar bell.
 *
 * Why this exists: the old bell subscribed to Hugo_Notifications
 * where userId == uid, but the only writer (threshold warnings from
 * oeSnapshotService) creates ORG-scoped docs with no userId at all —
 * so the bell matched zero documents forever.
 *
 * This hook merges two streams:
 *   1. personal   — docs targeted at this uid (future-proofing)
 *   2. org-scoped — threshold warnings for orgs the user belongs to,
 *      fenced to the viewer's locked scope (a Houston site manager
 *      never sees Cuenca warnings) and collapsed per
 *      scope+quadrant+day, mirroring WF8b's email dedupe — snapshot
 *      refreshes write the same warning many times per day.
 *
 * Read-state: personal docs use the doc's own `read` flag; org docs
 * are shared, so per-user read state lives in localStorage.
 */

const readStoreKey = (uid) => `hugo_notif_read_${uid}`;

function loadReadSet(uid) {
  try {
    const raw = localStorage.getItem(readStoreKey(uid));
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveReadSet(uid, set, keepIds) {
  try {
    // Prune to ids still visible so the store never grows unbounded.
    const pruned = [...set].filter((id) => keepIds.has(id));
    localStorage.setItem(readStoreKey(uid), JSON.stringify(pruned));
  } catch {
    /* storage unavailable — read-state just won't persist */
  }
}

/** Is this org-scoped notification inside the viewer's fence? */
function inScope(notif, queryScope) {
  const locked = Boolean(queryScope?.siteId || queryScope?.deptId || queryScope?.nwtId);
  if (!locked) return true; // admins / unscoped members see everything
  const { type, id } = notif.scope || {};
  if (type === 'organization') return true;
  if (type === 'site') return id === queryScope.siteId;
  if (type === 'department') return id === queryScope.deptId;
  if (type === 'team') return id === queryScope.nwtId;
  return false;
}

/** Collapse repeated warnings: one entry per org+scope+quadrant+day (newest wins). */
function collapse(docs) {
  const byKey = new Map();
  for (const n of docs) {
    const day = n.createdAt ? n.createdAt.toISOString().slice(0, 10) : 'unknown';
    const key = n.type === 'threshold_exceeded'
      ? [n.organizationId, n.scope?.type, n.scope?.id, n.quadrant, day].join('|')
      : n.id;
    const prev = byKey.get(key);
    if (!prev || n.createdAt > prev.createdAt) byKey.set(key, n);
  }
  return [...byKey.values()];
}

function normalize(d) {
  const data = d.data();
  return {
    id: d.id,
    ...data,
    createdAt: data.createdAt?.toDate?.() || new Date(),
    // Threshold warnings should land the user on the dashboard.
    link: data.link || (data.type === 'threshold_exceeded' ? '/drew' : null),
  };
}

export function useNotificationFeed() {
  const { user } = useAuth();
  const { queryScope } = useDemoRole();
  const { organizations } = useUserOrganizations(user?.uid);

  const [personal, setPersonal] = useState([]);
  const [orgDocs, setOrgDocs] = useState([]);
  const [readTick, setReadTick] = useState(0); // bump to re-read localStorage

  // Key the memo on a VALUE (joined id string), not the `organizations` array
  // identity. Keying on the array caused the org-notifications listener below
  // to unsubscribe/resubscribe on every render — a self-sustaining loop
  // (snapshot → setState → re-render → new deps → resubscribe) that re-billed
  // the full 60-doc snapshot several times a second per open tab.
  const orgIdsKey = organizations.map((o) => o.id).slice(0, 10).join(','); // Firestore 'in' cap
  const orgIds = useMemo(
    () => (orgIdsKey ? orgIdsKey.split(',') : []),
    [orgIdsKey]
  );

  // Stream 1 — personal notifications (userId-targeted).
  useEffect(() => {
    if (!user?.uid) { setPersonal([]); return undefined; }
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(30)
    );
    return onSnapshot(q, (snap) => setPersonal(snap.docs.map(normalize)), () => setPersonal([]));
  }, [user?.uid]);

  // Stream 2 — org-scoped notifications for the user's organizations.
  useEffect(() => {
    if (!user?.uid || orgIds.length === 0) { setOrgDocs([]); return undefined; }
    const q = query(
      collection(db, COLLECTION),
      where('organizationId', 'in', orgIds),
      orderBy('createdAt', 'desc'),
      limit(60)
    );
    return onSnapshot(q, (snap) => setOrgDocs(snap.docs.map(normalize)), () => setOrgDocs([]));
  }, [user?.uid, orgIds]);

  const notifications = useMemo(() => {
    const readSet = user?.uid ? loadReadSet(user.uid) : new Set();
    // Shared org notifications written before this account existed (the seeded Magnum history)
    // are context, not a backlog — they count as read so a new tester's first bell is not a red
    // "30" (UI review 2026-09-21 §1). Anything after they joined is unread until they open it.
    const joinedAt = user?.metadata?.creationTime ? new Date(user.metadata.creationTime) : null;
    const fencedOrg = collapse(
      orgDocs.filter((n) => !n.userId && inScope(n, queryScope))
    ).map((n) => ({ ...n, read: readSet.has(n.id) || Boolean(joinedAt && n.createdAt < joinedAt), shared: true }));
    const merged = [...personal, ...fencedOrg];
    merged.sort((a, b) => b.createdAt - a.createdAt);
    return merged.slice(0, 30);
    // readTick forces recompute after local mark-as-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personal, orgDocs, queryScope, user?.uid, readTick]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const markAsRead = useCallback(async (notif) => {
    if (!user?.uid) return;
    if (notif.shared) {
      const set = loadReadSet(user.uid);
      set.add(notif.id);
      saveReadSet(user.uid, set, new Set([...set, notif.id]));
      setReadTick((t) => t + 1);
    } else {
      try {
        await updateDoc(doc(db, COLLECTION, notif.id), { read: true, readAt: Timestamp.now() });
      } catch (e) {
        console.error('Failed to mark notification as read:', e);
      }
    }
  }, [user?.uid]);

  const markAllAsRead = useCallback(async () => {
    if (!user?.uid) return;
    const unread = notifications.filter((n) => !n.read);
    const set = loadReadSet(user.uid);
    const visible = new Set(notifications.map((n) => n.id));
    await Promise.all(unread.map(async (n) => {
      if (n.shared) {
        set.add(n.id);
      } else {
        try {
          await updateDoc(doc(db, COLLECTION, n.id), { read: true, readAt: Timestamp.now() });
        } catch (e) {
          console.error('Failed to mark notification as read:', e);
        }
      }
    }));
    saveReadSet(user.uid, set, visible);
    setReadTick((t) => t + 1);
  }, [user?.uid, notifications]);

  return { notifications, unreadCount, markAsRead, markAllAsRead };
}

export default useNotificationFeed;
